import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { CheckCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ASSET_FIELDS = [
  { key: 'cash_in_bank_accounts', label: 'Cash in Bank Accounts' },
  { key: 'marketable_securities', label: 'Marketable Securities' },
  { key: 'cash_surrender_value_life_insurance', label: 'Cash Surrender Value of Life Insurance' },
  { key: 'retirement_accounts', label: 'Retirement Accounts' },
  { key: 'real_estate_owned', label: 'Real Estate Owned' },
  { key: 'other_assets', label: 'Other Assets' },
];

const LIABILITY_FIELDS = [
  { key: 'credit_cards', label: 'Credit Cards' },
  { key: 'installment_loans', label: 'Installment Loans' },
  { key: 'real_estate_loans', label: 'Real Estate Loans' },
  { key: 'taxes_owed', label: 'Taxes Owed' },
  { key: 'other_liabilities', label: 'Other Liabilities' },
];

const numVal = (v) => parseFloat(v) || 0;
const fmtMoney = (v) => `$${numVal(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PFSPublicForm = () => {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState({ name: '' });

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/pfs-link/${token}`)
      .then(res => { setInfo(res.data); setLoading(false); })
      .catch(() => { setError('This link is invalid or has expired.'); setLoading(false); });
  }, [token]);

  const totalAssets = ASSET_FIELDS.reduce((s, f) => s + numVal(data[f.key]), 0);
  const totalLiabilities = LIABILITY_FIELDS.reduce((s, f) => s + numVal(data[f.key]), 0);
  const netWorth = totalAssets - totalLiabilities;

  const handleSubmit = async () => {
    if (!data.name) { alert('Please enter your full legal name.'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/pfs-link/${token}`, { pfs_data: data });
      setSubmitted(true);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center">
          <p className="text-red-600 font-medium">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center">
          <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-800 mb-2">Thank You!</h2>
          <p className="text-slate-600">Your Personal Financial Statement has been submitted successfully. You may close this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-[#081120] rounded-t-2xl px-6 py-5">
          <h1 className="text-xl font-bold text-white">Quidity Direct</h1>
          <p className="text-sm text-slate-300 mt-1">Personal Financial Statement</p>
        </div>
        <div className="bg-white rounded-b-2xl shadow-lg p-6">
          {info?.business_name && (
            <p className="text-sm text-slate-500 mb-4">Application for: <strong className="text-slate-700">{info.business_name}</strong></p>
          )}

          <div className="mb-4">
            <label className="text-sm font-semibold text-slate-700 block mb-1">Your Full Legal Name *</label>
            <Input className="h-10" value={data.name} onChange={e => setData(d => ({ ...d, name: e.target.value }))} placeholder="Enter your full legal name" data-testid="pfs-public-name" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-bold text-green-700 uppercase tracking-wide mb-3 border-b border-green-200 pb-1">Assets</h3>
              {ASSET_FIELDS.map(f => (
                <div key={f.key} className="mb-3">
                  <label className="text-xs text-slate-600 block mb-1">{f.label}</label>
                  <Input type="number" value={data[f.key] ?? ''} placeholder="0.00"
                    onChange={e => setData(d => ({ ...d, [f.key]: e.target.value }))} data-testid={`pfs-public-${f.key}`} />
                </div>
              ))}
              <div className="flex justify-between py-2 bg-green-100 rounded-lg px-3 mt-2">
                <span className="text-sm font-bold text-green-800">Total Assets</span>
                <span className="text-sm font-bold text-green-800">{fmtMoney(totalAssets)}</span>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-bold text-red-700 uppercase tracking-wide mb-3 border-b border-red-200 pb-1">Liabilities</h3>
              {LIABILITY_FIELDS.map(f => (
                <div key={f.key} className="mb-3">
                  <label className="text-xs text-slate-600 block mb-1">{f.label}</label>
                  <Input type="number" value={data[f.key] ?? ''} placeholder="0.00"
                    onChange={e => setData(d => ({ ...d, [f.key]: e.target.value }))} data-testid={`pfs-public-${f.key}`} />
                </div>
              ))}
              <div className="flex justify-between py-2 bg-red-100 rounded-lg px-3 mt-2">
                <span className="text-sm font-bold text-red-800">Total Liabilities</span>
                <span className="text-sm font-bold text-red-800">{fmtMoney(totalLiabilities)}</span>
              </div>
            </div>
          </div>

          <div className="mt-4 mb-6">
            <div className="flex justify-between py-3 px-4 bg-[#081120] rounded-xl">
              <span className="text-base font-bold text-white">Net Worth</span>
              <span className={`text-base font-bold ${netWorth >= 0 ? 'text-green-400' : 'text-red-400'}`}>{fmtMoney(netWorth)}</span>
            </div>
          </div>

          <Button onClick={handleSubmit} disabled={submitting} className="w-full h-11 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold" data-testid="pfs-public-submit">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
            {submitting ? 'Submitting...' : 'Submit Personal Financial Statement'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PFSPublicForm;
